// Zod-shape introspection for the LLM-facing op reference.
//
// Every op is a `z.object({ name: z.literal(NAME), params: <shape>,
// ...kwargs })` registered in a scope. This module turns that schema
// back into a human/LLM-readable signature: the positional `params`
// shape plus a typed, documented entry per keyword argument. Ported
// from the pre-engine `doc.tsx` walker (deleted in 18faf996) and
// adapted to the new op-registry shapes.
//
// Zod 4 keeps schema metadata under `_zod.def`. We read a handful of
// fields; tighter typing isn't worth chasing the full internal surface.

import { z } from 'zod';

import type { EnumDomainResolver } from './types.ts';

// Zod 4 marks `.int()` with a `number_format` check whose `format` is
// `safeint` (older builds used `check: 'int'`); accept either.
type ZodCheck = { readonly _zod?: { readonly def?: { readonly check?: string; readonly format?: string } } };
const isIntCheck = (c: ZodCheck): boolean => c._zod?.def?.check === 'int' || c._zod?.def?.format === 'safeint';
type ZodDef = {
  readonly type?: string;
  readonly innerType?: z.ZodType;
  readonly in?: z.ZodType;
  readonly out?: z.ZodType;
  readonly options?: readonly z.ZodType[];
  readonly element?: z.ZodType;
  readonly items?: readonly z.ZodType[];
  readonly rest?: z.ZodType | null;
  readonly values?: readonly unknown[];
  readonly entries?: Readonly<Record<string, string>>;
  readonly shape?: Readonly<Record<string, z.ZodType>>;
  readonly checks?: readonly ZodCheck[];
};

const zdef = (s: z.ZodType): ZodDef | undefined => (s as unknown as { _zod?: { def?: ZodDef } })._zod?.def;

// `optional()` / `default()` / `prefault()` flag the slot as omittable;
// `nullable()` / `readonly()` are transparent wrappers for doc purposes.
const OMITTABLE = new Set(['optional', 'default', 'prefault']);
const WRAPPERS = new Set([...OMITTABLE, 'nullable', 'readonly', 'nonoptional']);

const isWrapper = (s: z.ZodType): boolean => WRAPPERS.has(zdef(s)?.type ?? '');

/** True when the slot may be omitted by the caller (`.optional()` / `.default()`). */
export const isOptionalSchema = (s: z.ZodType): boolean => OMITTABLE.has(zdef(s)?.type ?? '');

// Peel wrappers and pipes down to the schema the caller actually
// writes. For a `.transform()`/`.pipe()` chain the *input* side (`in`)
// is the surface the LLM authors, so we descend into it.
const unwrap = (s: z.ZodType): z.ZodType => {
  const d = zdef(s);
  if (d === undefined) {
    return s;
  }
  if (isWrapper(s) && d.innerType !== undefined) {
    return unwrap(d.innerType);
  }
  if (d.type === 'pipe' && d.in !== undefined) {
    return unwrap(d.in);
  }
  return s;
};

/** The `.describe()` text registered on a schema (or its unwrapped core). */
export const describeOf = (s: z.ZodType): string | undefined =>
  z.globalRegistry.get(s)?.description ?? z.globalRegistry.get(unwrap(s))?.description;

const isSymNode = (s: z.ZodType): boolean => {
  const d = zdef(s);
  return d?.type === 'object' && d.shape !== undefined && '$sym' in d.shape;
};

// `def.type` → label renderer. Predicate-table dispatch (the engine's
// convention for tagged-union rendering — cf. `describeColumnType`).
// `s` is the unwrapped schema, `d` its def; `rec` recurses on a child.
const LABEL_BY_TYPE: Readonly<
  Record<string, (s: z.ZodType, d: ZodDef, rec: (c: z.ZodType) => string, resolve?: EnumDomainResolver) => string>
> = {
  string: () => '<string>',
  number: (_s, d) => (d.checks?.some(isIntCheck) ? '<integer>' : '<number>'),
  boolean: () => 'true | false',
  literal: (_s, d) => (d.values ?? []).map((v) => JSON.stringify(v)).join(' | '),
  // Enum-typed slot: render the registered domain *name* (e.g. `Color`)
  // when the members form a known domain — values are expanded once in
  // the "Enum domains" glossary — else fall back to the member join
  // (custom / test enums with no registered domain).
  enum: (_s, d, _rec, resolve) => {
    const members = Object.keys(d.entries ?? {});
    return resolve?.(members) ?? members.join(' | ');
  },
  array: (_s, d, rec) => (d.element ? `${rec(d.element)} …` : '<value> …'),
  tuple: (_s, d, rec) => `[${(d.items ?? []).map(rec).join(' ')}]`,
  unknown: () => '<expr>',
  any: () => '<expr>',
  object: (s) => (isSymNode(s) ? '<symbol>' : '(…)'),
  union: (_s, d, rec) => {
    const opts = d.options ?? [];
    // `symEnum(...)` is a union one of whose options is the bare
    // `z.enum(...)` — render the member list.
    const enumOpt = opts.map(unwrap).find((o) => zdef(o)?.type === 'enum');
    if (enumOpt) {
      return rec(enumOpt);
    }
    const labels = [...new Set(opts.map(rec))];
    // `symStr` is `string | {$sym}` — collapse to one identifier label.
    if (labels.every((l) => l === '<string>' || l === '<symbol>')) {
      return '<identifier>';
    }
    return labels.join(' | ');
  },
};

/**
 * Render a schema as a compact type label, e.g. `<integer>`,
 * `kpi | gauge`, `[<integer> <integer>]`, `<identifier>`.
 * Best-effort with a `<value>` fallback — the per-kwarg description
 * carries the precise semantics.
 */
export function typeLabel(schema: z.ZodType, resolve?: EnumDomainResolver): string {
  const s = unwrap(schema);
  const d = zdef(s);
  const render = d?.type === undefined ? undefined : LABEL_BY_TYPE[d.type];
  if (render === undefined || d === undefined) {
    return '<value>';
  }
  const rec = (c: z.ZodType): string => typeLabel(c, resolve);
  return render(s, d, rec, resolve);
}

const objectShape = (schema: z.ZodType): Readonly<Record<string, z.ZodType>> | undefined => {
  const d = zdef(unwrap(schema));
  return d?.type === 'object' ? d.shape : undefined;
};

/** One documented keyword argument of an op. */
export type KwargDoc = {
  readonly name: string;
  readonly type: string;
  readonly optional: boolean;
  readonly description: string | undefined;
};

/**
 * Every keyword argument of an op schema (the object shape minus the
 * reserved `name` discriminator and the positional `params` slot),
 * in declaration order.
 */
export function collectKwargs(schema: z.ZodType, resolve?: EnumDomainResolver): readonly KwargDoc[] {
  const shape = objectShape(schema);
  if (shape === undefined) {
    return [];
  }
  return Object.entries(shape)
    .filter(([key]) => key !== 'name' && key !== 'params')
    .map(([name, sch]) => ({
      name,
      type: typeLabel(sch, resolve),
      optional: isOptionalSchema(sch),
      description: describeOf(sch),
    }));
}

/**
 * The positional-argument signature of an op, e.g. `<string>` for
 * `params: z.tuple([z.string()])` or `<number> …` for a variadic
 * number array. Empty string when the op takes no positionals
 * (`params: z.tuple([])`).
 */
export function paramsSignature(schema: z.ZodType, resolve?: EnumDomainResolver): string {
  const shape = objectShape(schema);
  const params = shape?.params;
  if (params === undefined) {
    return '';
  }
  const d = zdef(unwrap(params));
  if (d?.type === 'tuple') {
    return (d.items ?? []).map((i) => typeLabel(i, resolve)).join(' ');
  }
  if (d?.type === 'array') {
    return d.element ? `${typeLabel(d.element, resolve)} …` : '<value> …';
  }
  return typeLabel(params, resolve);
}
